import { useEffect, useRef, useState } from "react"
import { Link } from "react-router"

import { cn } from "@/lib/utils"

interface NavbarProps {
  /**
   * Page title shown in the top-left. Defaults to the product name — each
   * page can pass a specific label (e.g. "TikTok Accounts").
   */
  title?: string
  /** Opens the left sidebar drawer. Provided by AppShell. */
  onOpenSidebar: () => void
  /**
   * Currently logged-in username. `null` while auth is loading, undefined
   * if the user is unauthenticated (shouldn't happen inside <AppShell/>
   * because <ProtectedRoute/> redirects first).
   */
  username: string | null
  onLogout: () => void
  /** Optional tabs/actions rendered next to the burger (left of title). */
  leftAction?: React.ReactNode
  /** Optional action rendered on the right side, e.g. "Refresh Balances". */
  rightAction?: React.ReactNode
}

/**
 * Ported from `backend/templates/*.html` — every Jinja page shared the same
 * `.navbar` block wrapping a burger + title on the left and a username
 * dropdown on the right. This is the single React equivalent. Pages control
 * the title and optional right-side action via props.
 */
export function Navbar({
  title = "Dashboard",
  onOpenSidebar,
  username,
  onLogout,
  leftAction,
  rightAction,
}: NavbarProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close the dropdown on click-outside (anywhere not inside the wrapper)
  // and on Escape. Mounted only while the dropdown is open so the
  // listeners don't run on every navbar render.
  useEffect(() => {
    if (!dropdownOpen) return
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        window.setTimeout(() => setDropdownOpen(false), 0)
      }
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDropdownOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handleClick)
      document.removeEventListener("keydown", handleKey)
    }
  }, [dropdownOpen])

  return (
    <nav
      className={cn(
        "flex shrink-0 items-center justify-between gap-3 px-3 py-2.5 sm:px-4",
        "bg-[#3a3a3a] border-b border-[#4a4a4a]",
        "shadow-[0_2px_8px_rgba(0,0,0,0.3)]",
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={onOpenSidebar}
          aria-label="Open navigation"
          className="flex shrink-0 flex-col gap-1 rounded-md p-2 transition-colors hover:bg-white/5 md:hidden"
        >
          <span className="block h-0.5 w-5 bg-brand-fg" />
          <span className="block h-0.5 w-5 bg-brand-fg" />
          <span className="block h-0.5 w-5 bg-brand-fg" />
        </button>
        {leftAction}
        {/* Site title is rendered as a span (not h1) so each page can own
            the document outline with its own h1 in the main content.
            Empty title hides the label (e.g. Ads Manager). */}
        {title ? (
          <span className="truncate text-2xl font-bold text-brand-accent">
            {title}
          </span>
        ) : null}
      </div>

      <div ref={dropdownRef} className="relative flex shrink-0 items-center gap-3">
        {rightAction}
        <button
          type="button"
          onClick={() => setDropdownOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={dropdownOpen}
          className={cn(
            "rounded-md border border-[#8898ff]/40 px-2.5 py-1 text-xs font-semibold",
            "bg-gradient-to-b from-[#93a1ff] to-[#7487f8] text-white",
            "shadow-[0_2px_8px_rgba(90,110,255,0.3)] transition-all",
            "hover:from-[#9caaFF] hover:to-[#7f91ff] hover:shadow-[0_3px_10px_rgba(90,110,255,0.4)]",
          )}
        >
          {username ?? "Loading..."}
        </button>
        {dropdownOpen && (
          <div
            className={cn(
              "absolute right-0 top-full mt-2 min-w-[160px] overflow-hidden z-50",
              "bg-[#3a3a3a] border border-[#4a4a4a] rounded-lg",
              "shadow-[0_4px_12px_rgba(0,0,0,0.4)]",
            )}
          >
            <Link
              to="/profile"
              className="block px-4 py-3 text-sm text-brand-fg hover:bg-[#444]"
              onClick={() => setDropdownOpen(false)}
            >
              Profile
            </Link>
            <button
              type="button"
              onClick={() => {
                setDropdownOpen(false)
                onLogout()
              }}
              className={cn(
                "block w-full text-left px-4 py-3 text-sm font-medium",
                "text-[#f87171] border-t border-[#4a4a4a]",
                "hover:bg-[#4a2a2a] hover:text-[#ff6b6b]",
              )}
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </nav>
  )
}
